/**
 * Layout that incorporates the sidebar for certain listing and details views.
 * Will render full width if the sidebar should be displayed.
 */
import { createComponent } from "react-fela";
import { gutters, layout } from "~/styleguide";
import { fluid } from "~/styleguide/utils";
import dynamic from "next/dynamic";
import React, { useContext, useEffect, useState } from "react";

// Components
import { ContentMaxWidth, SiteMaxWidth } from "~/components/layout/max-width";

// Contexts
import { LayoutContext, ResourceContext } from "~/context/content";

// Side bar only exists on specific resource listing or details
const SIDEBAR_CONFIG = [
    "listing:posts",
    "detail:posts",
    "listing:news",
    "detail:news",
    "detail:glossary",
    "detail:casestudies",
    "detail:podcasts",
    "detail:revenueexchange",
    "listing:events",
    "detail:events"
];

const Sidebar = dynamic(() => import("~/components/resource/sidebar"), {
    ssr: false
});

export default function SidebarColumn({
    children,
    // isPaginated,
    hasBlocks,
    extend,
    type,
    contentWithIds,
    hideSidebar
}) {
    const layout = useContext(LayoutContext);
    const contextType = useContext(ResourceContext);
    const hasSidebar = !hideSidebar && shouldShowSidebar(layout, contextType);
    const MaxWidth = hideSidebar ? SiteMaxWidth : ContentMaxWidth;

    return (
        <Container
            // isPaginated={isPaginated}
            hasBlocks={hasBlocks}
            extend={extend}
        >
            <MaxWidth extend={sidebarLayout(hasSidebar)}>
                <MainContent hasSidebar={hasSidebar}>{children}</MainContent>
                {hasSidebar && (
                    <DeferredSidebar
                        type={type} // Pass type to Sidebar
                        contentWithIds={contentWithIds} // Pass processed content with IDs
                     />
                )}
            </MaxWidth>
        </Container>
    );
}

function DeferredSidebar(props) {
    const [shouldRender, setShouldRender] = useState(false);

    useEffect(() => {
        if (typeof window === "undefined") return;

        const isTabletOrMobile =
            window.matchMedia &&
            window.matchMedia("(max-width: 1024px)").matches;

        if (isTabletOrMobile) return;

        let idleId = null;
        let timeoutId = null;

        const renderSidebar = () => {
            const schedule =
                window.requestIdleCallback ||
                (callback => {
                    timeoutId = setTimeout(callback, 1);
                    return timeoutId;
                });

            idleId = schedule(() => setShouldRender(true));
        };

        if (document.readyState === "complete") {
            renderSidebar();
        } else {
            window.addEventListener("load", renderSidebar, { once: true });
        }

        return () => {
            window.removeEventListener("load", renderSidebar);

            if (window.cancelIdleCallback && idleId) {
                window.cancelIdleCallback(idleId);
            }

            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, []);

    if (!shouldRender) return null;

    return <Sidebar {...props} />;
}

const shouldShowSidebar = (layout, type) => {
    if (!layout || !type) {
        return false;
    }
    const key = `${layout}:${type}`;
    return SIDEBAR_CONFIG.includes(key);
};

const sidebarLayout = hasSidebar => ({
    extend: [
        {
            condition: hasSidebar,
            style: {
                display: "flex",
                justifyContent: "space-between",
                maxWidth: layout.marqueeContentMaxWidth,
                maxWidthTablet: {
                    flexWrap: "wrap"
                }
            }
        }
    ]
});

const Container = createComponent(({ hasBlocks }) => ({
    extend: [
        fluid("paddingH", gutters.default),
        fluid("paddingTop", gutters.verticalNarrow),
        fluid("paddingBottom", gutters.verticalNarrow),
        {
            condition: hasBlocks,
            style: {
                paddingV: "0px !important"
            }
        }
    ]
}));

const MainContent = createComponent(({ hasSidebar }) => ({
    maxWidth: "100%",
    extend: [
        {
            condition: hasSidebar,
            style: {
                width: "calc(65% - 50px)",
                maxWidthTablet: {
                    width: "100%"
                }
            }
        }
    ]
}));
